import React, { createContext, useState, useEffect, useCallback, useContext, useMemo } from 'react'

import { STEAM_ITEM_IMAGES_URL, STEAM_ITEM_IMAGE_URL } from '../constants/steam'
import { getSkinSteamMarketHashName } from '../utils/getSkinNames'
import type { SkinProps } from '../types/skin'

export const ImageContext = createContext<{
  imageMap: Record<string, string> | null
  isLoading: boolean
}>({
  imageMap: null,
  isLoading: true,
})

export function SteamImageProvider({ children }: { children: React.ReactNode }) {
  const [imageMap, setImageMap] = useState<Record<string, string> | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const fetchAllImages = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await fetch(`${STEAM_ITEM_IMAGES_URL}`)
      const map = await response.json()
      setImageMap(map)
    } catch (error) {
      // TODO: pop up the error message
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAllImages()
  }, [fetchAllImages])

  return <ImageContext.Provider value={{ imageMap, isLoading }}>{children}</ImageContext.Provider>
}

export function useSteamImageUrl({
  itemName,
  skinName,
  exterior,
  specialty,
  phase,
  category,
  tag,
}: SkinProps) {
  const { imageMap, isLoading } = useContext(ImageContext)

  const [marketHashName, setMarketHashName] = useState('')

  useEffect(() => {
    setMarketHashName(
      getSkinSteamMarketHashName(itemName, skinName, exterior, specialty, phase, category, tag),
    )
  }, [itemName, skinName, exterior, specialty, phase, category, tag])

  const imageUrl = useMemo(() => {
    if (marketHashName && imageMap) {
      const fallbackUrl = `${STEAM_ITEM_IMAGE_URL}/${marketHashName}`
      return imageMap[marketHashName] || fallbackUrl
    }
    return ''
  }, [marketHashName, imageMap])

  return { imageUrl, isLoading, marketHashName }
}
